import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { API, mondayOf, addDays, isoDate, fmtDay } from './_helpers';

export default function RosterPublicSchedulePage() {
  const { token } = useParams();
  const [anchor, setAnchor] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const days = useMemo(() => Array.from({length: 7}, (_, i) => isoDate(addDays(anchor, i))), [anchor]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/roster/public/${token}?from=${days[0]}&to=${days[6]}`)
      .then(r => { setData(r.data); setErr(''); })
      .catch(e => setErr(e?.response?.data?.detail || 'Schedule not available'))
      .finally(() => setLoading(false));
  }, [token, anchor.getTime()]);

  const siteById = useMemo(() => Object.fromEntries((data?.sites || []).map(s => [s.site_id, s])), [data]);

  // Group shifts by date
  const byDate = useMemo(() => {
    const m = {};
    (data?.shifts || []).forEach(s => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-1" style={{ color: '#52c3c5' }}>
              I3 RADIOLOGY · PUBLIC ROSTER
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#0d2644' }}>
              {data?.title || 'Team roster'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Read-only view of the team week. Updates as shifts are published.</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setAnchor(addDays(anchor, -7))} data-testid="public-prev-week"
                    className="p-1.5 rounded border hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setAnchor(mondayOf(new Date()))} data-testid="public-today"
                    className="text-xs font-semibold px-3 py-1.5 rounded border hover:bg-muted">This week</button>
            <button onClick={() => setAnchor(addDays(anchor, 7))} data-testid="public-next-week"
                    className="p-1.5 rounded border hover:bg-muted"><ChevronRight className="w-4 h-4" /></button>
            <div className="ml-3 text-sm font-semibold" style={{ color: '#0d2644' }}>
              {fmtDay(days[0])} → {fmtDay(days[6])}
            </div>
          </div>
        </div>

        {err && (
          <div className="text-center py-16 border rounded-xl bg-card">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
            <p className="font-semibold">{err}</p>
            <p className="text-xs text-muted-foreground mt-1">Ask the practice manager for the current link.</p>
          </div>
        )}

        {loading && <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>}

        {!loading && !err && data && (
          <>
            {data.sites && data.sites.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap mb-4 text-xs text-muted-foreground">
                <span className="font-semibold uppercase tracking-wider">Sites:</span>
                {data.sites.map(s => (
                  <span key={s.site_id} className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    {s.name}
                  </span>
                ))}
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-7">
              {days.map(d => {
                const isToday = d === isoDate(new Date());
                const ds = byDate[d] || [];
                return (
                  <div key={d} className={`border rounded-lg bg-card p-2 min-h-[160px] ${isToday ? 'ring-2 ring-[#52c3c5]' : ''}`}
                       data-testid={`public-day-${d}`}>
                    <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: '#0d2644' }}>
                      {new Date(d+'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                    {ds.length === 0 && <div className="text-[11px] text-muted-foreground italic">No shifts</div>}
                    {ds.map(s => {
                      const site = s.site_id ? siteById[s.site_id] : null;
                      return (
                        <div key={s.shift_id} className="rounded text-[11px] px-1.5 py-1 mb-1 leading-tight"
                             style={{ background: site ? site.color : '#475569', color: '#fff' }}>
                          <div className="font-bold">{s.start}–{s.end}</div>
                          <div className="opacity-95 truncate">{s.user_name}</div>
                          {s.role_label && <div className="opacity-85 text-[10px] truncate">{s.role_label}</div>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-6 text-center">
              i3 Radiology Assistant · Updates publish automatically.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
